/**
 * Live updates over the realtime Worker's WebSocket. One connection for the app, opened
 * with a short-lived ticket from the API and reopened (with a new ticket) whenever it
 * drops. Events are hints: screens refetch from the API when they hear about a change.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { post } from "./api";
import type { RealtimeEvent, RealtimeTicket } from "./types";

type Listener = (event: RealtimeEvent | { type: "reconnected" }) => void;

const listeners = new Set<Listener>();
const statusListeners = new Set<(connected: boolean) => void>();
let socket: WebSocket | null = null;
let connected = false;
let attempts = 0;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let pingTimer: ReturnType<typeof setInterval> | undefined;
let everConnected = false;
let connecting = false;

function setConnected(value: boolean) {
  connected = value;
  for (const listener of statusListeners) listener(value);
}

function emit(event: RealtimeEvent | { type: "reconnected" }) {
  for (const listener of listeners) listener(event);
}

async function connect() {
  clearTimeout(retryTimer);
  if (socket || connecting || listeners.size === 0) return;
  let ticket: RealtimeTicket;
  connecting = true;
  try {
    ticket = await post<RealtimeTicket>("/realtime/ticket");
  } catch {
    scheduleRetry();
    return;
  } finally {
    connecting = false;
  }
  if (listeners.size === 0 || socket) return;
  const ws = new WebSocket(`${ticket.url}?ticket=${encodeURIComponent(ticket.ticket)}`);
  socket = ws;
  ws.onopen = () => {
    attempts = 0;
    setConnected(true);
    // Anything missed while disconnected: refetch.
    if (everConnected) emit({ type: "reconnected" });
    everConnected = true;
    pingTimer = setInterval(() => ws.send("ping"), 30_000);
  };
  ws.onmessage = (message) => {
    if (typeof message.data !== "string" || message.data === "pong") return;
    try {
      emit(JSON.parse(message.data) as RealtimeEvent);
    } catch {
      // Not ours.
    }
  };
  ws.onclose = () => {
    clearInterval(pingTimer);
    if (socket === ws) socket = null;
    setConnected(false);
    scheduleRetry();
  };
  ws.onerror = () => ws.close();
}

function scheduleRetry() {
  if (listeners.size === 0) return;
  const delays = [1_000, 2_000, 5_000, 10_000, 30_000];
  const delay = delays[Math.min(attempts, delays.length - 1)]!;
  attempts += 1;
  clearTimeout(retryTimer);
  retryTimer = setTimeout(connect, delay);
}

function disconnect() {
  clearTimeout(retryTimer);
  clearInterval(pingTimer);
  const ws = socket;
  socket = null;
  ws?.close(1000);
  setConnected(false);
}

/** Back in the foreground (phones suspend background tabs): reconnect straight away. */
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && !socket && listeners.size > 0) {
      attempts = 0;
      void connect();
    }
  });
}

/** Listens for live events while the component is mounted. */
export function useRealtime(listener: Listener, enabled = true): { connected: boolean } {
  const [isConnected, setIsConnected] = useState(connected);
  const latest = useRef(listener);
  useLayoutEffect(() => {
    latest.current = listener;
  });
  useEffect(() => {
    if (!enabled) return;
    const forward: Listener = (event) => latest.current(event);
    listeners.add(forward);
    statusListeners.add(setIsConnected);
    setIsConnected(connected);
    void connect();
    return () => {
      listeners.delete(forward);
      statusListeners.delete(setIsConnected);
      if (listeners.size === 0) disconnect();
    };
  }, [enabled]);
  return { connected: isConnected };
}

/** Closes the connection (signing out). */
export function closeRealtime() {
  listeners.clear();
  disconnect();
}
